import React, { useState } from 'react';
import { useAudienceQuery } from 'src/api/queries/deliveryAnalyticsQueries';
import {
  AudienceDimension,
  DeliveryAnalyticsFilters,
} from 'src/types/TDeliveryAnalytics';
import { formatBillingBytes, formatNumberIntl } from 'src/utils/common';
import { AsyncSection, DataAsOfBadge, Section } from '../components/common';
import styles from '../styles.module.scss';

const DIMENSIONS: { value: AudienceDimension; label: string }[] = [
  { value: 'country', label: 'Country' },
  { value: 'device', label: 'Device' },
  { value: 'os', label: 'OS' },
  { value: 'browser', label: 'Browser' },
  { value: 'referrer', label: 'Referrer' },
  { value: 'asn', label: 'Network (ASN)' },
  { value: 'hour_of_day', label: 'Hour of day' },
];

const AudienceTab: React.FC<{ filters: DeliveryAnalyticsFilters }> = ({ filters }) => {
  const [dimension, setDimension] = useState<AudienceDimension>('country');
  const { data, isLoading, error } = useAudienceQuery({ ...filters, dimension, limit: 100 });
  const rows = data?.data ?? [];
  const max = rows.reduce((m, r) => Math.max(m, r.requests), 0);

  return (
    <Section
      title="Audience breakdown"
      description="Who is requesting your delivered media, by dimension."
      headerRight={
        <div className={styles.inlineControls}>
          <select
            className={styles.select}
            value={dimension}
            onChange={(e) => setDimension(e.target.value as AudienceDimension)}
          >
            {DIMENSIONS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
          <DataAsOfBadge value={data?.data_as_of} loading={isLoading} />
        </div>
      }
    >
      <AsyncSection isLoading={isLoading} error={error} isEmpty={rows.length === 0}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Value</th>
              <th className={styles.numCol}>Requests</th>
              <th className={styles.numCol}>Bytes</th>
              <th className={styles.barCol}>Share</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.dimension || '(empty)'}>
                <td>{r.dimension || '(unknown)'}</td>
                <td className={styles.numCol}>{formatNumberIntl(r.requests)}</td>
                <td className={styles.numCol}>{formatBillingBytes(r.bytes)}</td>
                <td className={styles.barCol}>
                  <span
                    className={styles.bar}
                    style={{ width: max > 0 ? `${(r.requests / max) * 100}%` : '0%' }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </AsyncSection>
    </Section>
  );
};

export default AudienceTab;
